import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = ['input', 'row', 'noResults', 'bookingFilter', 'header', 'tbody']

  connect() {
    this.debounceTimeout = null
    this.sortColumn = null
    this.sortDirection = null // null, 'asc', 'desc'
    this.originalOrder = [...this.rowTargets]
  }

  disconnect() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }
  }

  sort(event) {
    const header = event.currentTarget
    const column = Number(header.dataset.column)

    // Cycle: null -> asc -> desc -> null
    if (this.sortColumn !== column) {
      this.sortColumn = column
      this.sortDirection = 'asc'
    } else if (this.sortDirection === 'asc') {
      this.sortDirection = 'desc'
    } else if (this.sortDirection === 'desc') {
      this.sortColumn = null
      this.sortDirection = null
    }

    this.updateSortIndicators()
    this.applySort()
  }

  applySort() {
    const tbody = this.tbodyTarget
    let rows

    if (this.sortColumn === null) {
      // Reset to original order
      rows = [...this.originalOrder]
    } else {
      rows = [...this.rowTargets].sort((a, b) => {
        const aValue = this.getCellValue(a, this.sortColumn)
        const bValue = this.getCellValue(b, this.sortColumn)
        const comparison = this.naturalCompare(aValue, bValue)
        return this.sortDirection === 'asc' ? comparison : -comparison
      })
    }

    rows.forEach(row => tbody.appendChild(row))
  }

  getCellValue(row, columnIndex) {
    const cell = row.children[columnIndex]
    return cell ? cell.textContent.trim().toLowerCase() : ''
  }

  naturalCompare(a, b) {
    return a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' })
  }

  updateSortIndicators() {
    this.headerTargets.forEach(header => {
      const indicator = header.querySelector('[data-sort-indicator]')
      if (!indicator) return

      const active = Number(header.dataset.column) === this.sortColumn
      indicator.classList.toggle('beachy-sort-arrow-idle', !active)
      if (active) {
        indicator.textContent = this.sortDirection === 'asc' ? '↑' : '↓'
      } else {
        indicator.textContent = '↕'
      }
    })
  }

  search() {
    if (this.debounceTimeout) {
      clearTimeout(this.debounceTimeout)
    }

    this.debounceTimeout = setTimeout(() => {
      this.filterRows()
    }, 150)
  }

  filterByBooking(event) {
    this.bookingFilterTargets.forEach(tab => {
      const active = tab === event.currentTarget
      tab.classList.toggle('beachy-segmented-tab-active', active)
      tab.setAttribute('aria-pressed', active)
    })
    this.filterRows()
  }

  filterRows() {
    const query = this.inputTarget.value.trim().toLowerCase()
    const bookingFilter = this.selectedBookingFilter
    let visibleCount = 0

    this.rowTargets.forEach(row => {
      const matchesSearch = query === '' || row.textContent.toLowerCase().includes(query)
      const matchesBooking = this.matchesBookingFilter(row, bookingFilter)
      const isVisible = matchesSearch && matchesBooking

      row.style.display = isVisible ? '' : 'none'
      if (isVisible) visibleCount += 1
    })

    if (this.hasNoResultsTarget) {
      this.noResultsTarget.hidden = visibleCount !== 0
    }
  }

  matchesBookingFilter(row, filter) {
    if (filter === 'all') return true

    const isBooked = row.dataset.booked === 'true'
    return filter === 'booked' ? isBooked : !isBooked
  }

  get selectedBookingFilter() {
    const active = this.bookingFilterTargets.find(tab => tab.classList.contains('beachy-segmented-tab-active'))
    return active ? active.dataset.value : 'all'
  }
}
